import { AutomationDefinition, TRIGGER_NODE_ID } from "./automation";

export interface GraphAnalysis {
  /** Topological order of nodes reachable from the trigger (trigger first). */
  order: string[];
  reachable: ReadonlySet<string>;
  cycle: string[] | null;
  predecessors: ReadonlyMap<string, string[]>;
  /** Nodes that run before this one on every path from the trigger. */
  always: ReadonlyMap<string, ReadonlySet<string>>;
  /** Nodes that run before this one on at least one path. */
  sometimes: ReadonlyMap<string, ReadonlySet<string>>;
}

export function analyzeGraph(automation: AutomationDefinition): GraphAnalysis {
  const ids = [TRIGGER_NODE_ID, ...automation.nodes.map((n) => n.id)];
  const successors = new Map<string, string[]>(ids.map((id) => [id, []]));
  const predecessors = new Map<string, string[]>(ids.map((id) => [id, []]));
  for (const edge of automation.edges) {
    successors.get(edge.from)?.push(edge.to);
    predecessors.get(edge.to)?.push(edge.from);
  }

  const reachable = new Set<string>();
  const stack = [TRIGGER_NODE_ID];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined || reachable.has(id)) {
      continue;
    }
    reachable.add(id);
    for (const next of successors.get(id) ?? []) {
      stack.push(next);
    }
  }

  const cycle = findCycle(ids, successors);
  const order: string[] = [];
  if (!cycle) {
    const inDegree = new Map<string, number>();
    for (const id of reachable) {
      inDegree.set(
        id,
        (predecessors.get(id) ?? []).filter((p) => reachable.has(p)).length,
      );
    }
    const queue = [TRIGGER_NODE_ID];
    while (queue.length > 0) {
      const id = queue.shift();
      if (id === undefined) {
        break;
      }
      order.push(id);
      for (const next of successors.get(id) ?? []) {
        if (!reachable.has(next)) {
          continue;
        }
        const remaining = (inDegree.get(next) ?? 0) - 1;
        inDegree.set(next, remaining);
        if (remaining === 0) {
          queue.push(next);
        }
      }
    }
  }

  const always = new Map<string, Set<string>>();
  const sometimes = new Map<string, Set<string>>();
  for (const id of order) {
    const reachablePredecessors = (predecessors.get(id) ?? []).filter((p) =>
      reachable.has(p),
    );
    if (reachablePredecessors.length === 0) {
      always.set(id, new Set());
      sometimes.set(id, new Set());
      continue;
    }
    let must: Set<string> | undefined;
    const may = new Set<string>();
    for (const p of reachablePredecessors) {
      const viaP = new Set([...(always.get(p) ?? []), p]);
      must = must ? new Set([...must].filter((x) => viaP.has(x))) : viaP;
      for (const x of sometimes.get(p) ?? []) {
        may.add(x);
      }
      may.add(p);
    }
    always.set(id, must ?? new Set());
    sometimes.set(id, may);
  }

  return { order, reachable, cycle, predecessors, always, sometimes };
}

function findCycle(
  ids: string[],
  successors: ReadonlyMap<string, string[]>,
): string[] | null {
  const state = new Map<string, "visiting" | "done">();
  // Iterative DFS (automations can be long enough to overflow the call stack).
  for (const start of ids) {
    if (state.has(start)) {
      continue;
    }
    const path: string[] = [start];
    const next: number[] = [0];
    state.set(start, "visiting");
    while (path.length > 0) {
      const depth = path.length - 1;
      const id = path[depth];
      const i = next[depth];
      if (id === undefined || i === undefined) {
        break;
      }
      const child = (successors.get(id) ?? [])[i];
      if (child === undefined) {
        state.set(id, "done");
        path.pop();
        next.pop();
        continue;
      }
      next[depth] = i + 1;
      const s = state.get(child);
      if (s === "visiting") {
        return [...path.slice(path.indexOf(child)), child];
      }
      if (s === undefined) {
        state.set(child, "visiting");
        path.push(child);
        next.push(0);
      }
    }
  }
  return null;
}
